import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import logoV from './assets/visionary-logo.svg';
import unityLogo from './assets/unity-logo.png';
import logo from './assets/blackfield-logo.svg';
import roundedPng from './assets/blackfield™_rounded.png';
// Publisher-specific images — not used on main site
import pub00 from './assets/pub_00.webp';
import pub01 from './assets/pub_01.webp';
import pub02 from './assets/pub_02.webp';
import pub03 from './assets/pub_03.webp';
import pub04 from './assets/pub_04.webp';
import pub05 from './assets/pub_05.webp';
import pub06 from './assets/pub_06.webp';
import pub07 from './assets/pub_07.webp';

const PASSWORD_HASH = '027da0d2250f5a93057bd887b20b293b9625c8a7d89296eca2b6fce661e79d20';
const SESSION_KEY = 'bf_gate_auth';
const PDF_URL = '/blackfield-deck.pdf';

async function sha256(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

const SLIDES = [
  /* 00 — Cover */
  {
    id: '00',
    label: 'Cover',
    bg: pub00,
    content: (
      <div className="ps-slide ps-slide-cover">
        <div className="ps-cover-center">
          <img src={logo} alt="BLACKFIELD" className="ps-cover-logo" />
          <span className="ps-cover-badge">PUBLISHER PITCH DECK · 2026</span>
        </div>
        <div className="ps-cover-footer">
          <img src={logoV} alt="VISIONARY GAMES" className="ps-cover-footer-logo" />
          <span className="ps-cover-footer-credit">A OSADCHY YURI GAME</span>
          <div className="ps-cover-footer-unity">
            <img src={unityLogo} alt="Unity" className="ps-cover-footer-unity-logo" />
          </div>
        </div>
      </div>
    ),
  },

  /* 01 — The Game — image left, text right */
  {
    id: '01',
    label: 'The Game',
    content: (
      <div className="ps-slide">
        <div className="ps-split">
          <div className="ps-split-img">
            <img src={pub02} alt="Blackfield gameplay" />
          </div>
          <div className="ps-split-content">
            <h2 className="ps-slide-title">The Game</h2>
            <p className="ps-big-quote">
              "Marta carries food to people who are starving. She decides who gets it. Not everyone will."
            </p>
            <div className="ps-rows">
              {[
                { idx: 'GENRE', title: 'Management Adventure', desc: '2.5D, puzzle and narrative-driven. PC, Mac, and consoles.' },
                { idx: 'LOOP', title: 'Receive · Help · Survive', desc: 'Take on missions, deliver resources to starving communities, upgrade your gear, and decide who gets help when there isn\'t enough for everyone.' },
                { idx: 'STATUS', title: 'In Development', desc: 'All core systems built and playable. Actively seeking publisher partner.' },
              ].map(({ idx, title, desc }) => (
                <div key={idx} className="ps-row">
                  <span className="ps-row-idx">{idx}</span>
                  <strong className="ps-row-title">{title}</strong>
                  <p className="ps-row-desc">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    ),
  },

  /* 02 — Screenshots — full-width grid */
  {
    id: '02',
    label: 'Screenshots',
    content: (
      <div className="ps-slide ps-slide-full" style={{ padding: '6px' }}>
        <div className="ps-screenshots">
          {[pub01, pub02, pub03, pub05, pub06, pub07].map((img, i) => (
            <img key={i} src={img} alt={`Blackfield screenshot ${i + 1}`} className="ps-screenshot" />
          ))}
        </div>
      </div>
    ),
  },

  /* 03 — Trailer — full-screen iframe */
  {
    id: '03',
    label: 'Trailer',
    content: (
      <div className="ps-slide ps-slide-trailer">
        <iframe
          src="https://www.youtube.com/embed/owUpflFnaNQ"
          title="Blackfield Trailer"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
        />
      </div>
    ),
  },

  /* 04 — Market — text left, image right */
  {
    id: '04',
    label: 'Market',
    content: (
      <div className="ps-slide">
        <div className="ps-split">
          <div className="ps-split-content">
            <h2 className="ps-slide-title">Market & Comparables</h2>
            <div className="ps-rows">
              {[
                { idx: '3M+', title: 'This War of Mine', desc: '11 bit studios. Dark management survival, moral choices, civilian protagonist. Closest tonal and mechanical comp.' },
                { idx: '2M+', title: 'Spiritfarer', desc: 'Thunder Lotus. 2.5D narrative management — proof the market rewards emotionally driven resource games.' },
                { idx: '500K+', title: 'Endling: Extinction is Forever', desc: 'Herobeat Studios, 2022. 2.5D survival, dark emotional narrative, resource scarcity, moral weight. Same tone, same platform, same stakes.' },
              ].map(({ idx, title, desc }) => (
                <div key={idx} className="ps-row">
                  <span className="ps-row-idx" style={{ color: '#e4384f', fontSize: '1rem', fontWeight: 700 }}>{idx}</span>
                  <strong className="ps-row-title">{title}</strong>
                  <p className="ps-row-desc">{desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="ps-split-img">
            <img src={pub03} alt="Blackfield environment" />
          </div>
        </div>
      </div>
    ),
  },

  /* 05 — Status — image left, text right */
  {
    id: '05',
    label: 'Status',
    content: (
      <div className="ps-slide">
        <div className="ps-split">
          <div className="ps-split-img">
            <img src={pub04} alt="Blackfield gameplay" />
          </div>
          <div className="ps-split-content">
            <h2 className="ps-slide-title">Development Status</h2>
            <div className="ps-rows">
              {[
                { idx: 'DONE', title: 'Core Systems', desc: 'Resource management, weight physics, faction AI, task chain engine — fully functional.' },
                { idx: 'DONE', title: 'Vertical Slice', desc: 'Fully playable slice demonstrating core loop, art direction, and narrative tone. Playable demo available — contact to receive build.' },
                { idx: 'NEXT', title: 'Full Production', desc: '14–18 months to ship. 10 areas, 30+ NPCs, 8–12 hours of content with publisher partnership.' },
              ].map(({ idx, title, desc }) => (
                <div key={idx} className="ps-row">
                  <span className="ps-row-idx">{idx}</span>
                  <strong className="ps-row-title">{title}</strong>
                  <p className="ps-row-desc">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    ),
  },

  /* 06 — Roadmap — text left, image right */
  {
    id: '06',
    label: 'Roadmap',
    content: (
      <div className="ps-slide">
        <div className="ps-split">
          <div className="ps-split-content">
            <h2 className="ps-slide-title">Roadmap</h2>
            <div className="ps-rows">
              {[
                { idx: 'Q3 2026', title: 'Prototype Complete', desc: 'Core engine, art pipeline, and narrative framework established.' },
                { idx: 'Q2 2027', title: 'Alpha — Full Content', desc: 'All 10 areas playable. Full NPC roster, branching story, and audio pass. Publisher QA integration.' },
                { idx: 'Q3 2027', title: 'Ship', desc: 'PC/Mac launch on Steam. Console ports to follow based on publisher agreement.' },
              ].map(({ idx, title, desc }) => (
                <div key={idx} className="ps-row">
                  <span className="ps-row-idx">{idx}</span>
                  <strong className="ps-row-title">{title}</strong>
                  <p className="ps-row-desc">{desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="ps-split-img">
            <img src={pub05} alt="Blackfield environment" />
          </div>
        </div>
      </div>
    ),
  },

  /* 07 — Budget — image left, text right */
  {
    id: '07',
    label: 'Budget & Ask',
    content: (
      <div className="ps-slide">
        <div className="ps-split">
          <div className="ps-split-img">
            <img src={pub06} alt="Blackfield gameplay" />
          </div>
          <div className="ps-split-content">
            <h2 className="ps-slide-title">Budget & Ask</h2>
            <table className="ps-budget">
              <tbody>
                <tr><td>Development (18 months)</td><td className="ps-budget-cost">$122,000</td></tr>
                <tr><td>Marketing</td><td className="ps-budget-cost">$50,000</td></tr>
                <tr className="ps-budget-total"><td><strong>Total Ask</strong></td><td className="ps-budget-cost"><strong>$172,000</strong></td></tr>
              </tbody>
            </table>
            <p className="ps-budget-note">Milestone-based. Prototype delivered without funding — commitment and execution already proven.</p>
          </div>
        </div>
      </div>
    ),
  },

  /* 08 — Team — text left, image right */
  {
    id: '08',
    label: 'Team',
    content: (
      <div className="ps-slide">
        <div className="ps-split">
          <div className="ps-split-content">
            <h2 className="ps-slide-title">The Developer</h2>
            <div className="ps-rows">
              {[
                { idx: 'WHO', title: 'Osadchy Yuri — Solo Dev', desc: 'Product designer, 15 years of experience. Based in Bangkok, Thailand — remote-ready, full-time. Legal entity: Israel. Blackfield built from scratch, zero external funding.' },
                { idx: 'ENGINE', title: 'Unity — Custom Game Logic', desc: 'Built on Unity. Every game system designed and built from scratch specifically for Blackfield.' },
                { idx: 'PROOF', title: 'Vertical Slice Shipped', desc: 'Self-funded prototype: art, narrative, and UI/UX — all built solo.' },
              ].map(({ idx, title, desc }) => (
                <div key={idx} className="ps-row">
                  <span className="ps-row-idx">{idx}</span>
                  <strong className="ps-row-title">{title}</strong>
                  <p className="ps-row-desc">{desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="ps-split-img">
            <img src={pub07} alt="Blackfield environment" />
          </div>
        </div>
      </div>
    ),
  },

  /* 09 — Contact — image left, text right */
  {
    id: '09',
    label: 'Contact',
    content: (
      <div className="ps-slide">
        <div className="ps-split">
          <div className="ps-split-img">
            <img src={pub01} alt="Blackfield" />
          </div>
          <div className="ps-split-content">
            <h2 className="ps-slide-title">Let's Talk</h2>
            <div className="ps-rows">
              {[
                { idx: 'WHO', title: 'Osadchy Yuri', desc: 'Visionary Games — solo developer, founder.' },
                { idx: 'DEAL', title: 'Open to Full Publishing Deal', desc: '' },
                { idx: 'EMAIL', title: 'yuri@blackfieldgame.com', desc: '' },
                { idx: 'WEB', title: 'visionarygames.net', desc: <><em>Steam</em><br />store.steampowered.com/app/3157920</> },
              ].map(({ idx, title, desc }) => (
                <div key={idx} className="ps-row">
                  <span className="ps-row-idx">{idx}</span>
                  <strong className="ps-row-title">{title}</strong>
                  {desc && <p className="ps-row-desc">{desc}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    ),
  },
];

export default function PresentationPage() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(SESSION_KEY) === '1');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [slide, setSlide] = useState(0);

  const prev = useCallback(() => setSlide(s => Math.max(0, s - 1)), []);
  const next = useCallback(() => setSlide(s => Math.min(SLIDES.length - 1, s + 1)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next();
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev, next]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const hash = await sha256(password);
    if (hash === PASSWORD_HASH) {
      sessionStorage.setItem(SESSION_KEY, '1');
      setAuthed(true);
    } else {
      setError('Incorrect password.');
      setPassword('');
    }
  };

  const mobileNotice = (
    <div className="ps-mobile-notice">
      <Helmet>
        <title>Presentation – Blackfield | Visionary Games</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <img src={logo} alt="BLACKFIELD" className="ps-mobile-notice-logo" />
      <p className="ps-mobile-notice-text">This presentation is designed for desktop.</p>
      <p className="ps-mobile-notice-sub">Please open it on a laptop or desktop computer.</p>
    </div>
  );

  if (!authed) {
    return (
      <>
        {mobileNotice}
        <div className="ps-gate">
          <Helmet>
            <title>Presentation – Blackfield | Visionary Games</title>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
          <nav className="top-nav">
            <div className="top-nav-links">
              <Link to="/" className="top-nav-link top-nav-home">HOME</Link>
              <Link to="/news" className="top-nav-link">NEWS</Link>
              <Link to="/press-kit" className="top-nav-link">PRESS KIT</Link>
              <a href="mailto:yuri@blackfieldgame.com" className="top-nav-link">CONTACT</a>
              <span className="top-nav-link top-nav-disabled">ART BOOK</span>
              <Link to="/publisher" className="top-nav-link top-nav-presentation"><span className="material-symbols-rounded" style={{fontSize:'0.85rem',verticalAlign:'middle'}}>lock</span> PUBLISHER</Link>
            </div>
          </nav>
          <img src={roundedPng} alt="" className="ps-gate-bg-logo" aria-hidden="true" />
          <img src={logoV} alt="VISIONARY GAMES" className="ps-gate-logo" />
          <form className="ps-gate-form" onSubmit={handleLogin}>
            <input
              className="ps-gate-input"
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              autoFocus
            />
            {error && <span className="ps-gate-error">{error}</span>}
            <button className="ps-gate-btn" type="submit">ACCESS</button>
          </form>
        </div>
      </>
    );
  }

  const current = SLIDES[slide];
  const isFirst = slide === 0;
  const isLast = slide === SLIDES.length - 1;

  return (
    <>
      {mobileNotice}
      <div className="ps-wrap">
        <Helmet>
          <title>Presentation – Blackfield | Visionary Games</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <nav className="top-nav">
          <div className="top-nav-links">
            <Link to="/" className="top-nav-link top-nav-home">HOME</Link>
            <Link to="/news" className="top-nav-link">NEWS</Link>
            <Link to="/press-kit" className="top-nav-link">PRESS KIT</Link>
            <a href="mailto:yuri@blackfieldgame.com" className="top-nav-link">CONTACT</a>
            <span className="top-nav-link top-nav-disabled">ART BOOK</span>
            <Link to="/publisher" className="top-nav-link top-nav-presentation"><span className="material-symbols-rounded" style={{fontSize:'0.85rem',verticalAlign:'middle'}}>lock_open</span> PUBLISHER</Link>
          </div>
        </nav>

        <div className="ps-outer-box">
          <div className="ps-inner-box">
            {current.bg && (
              <img src={current.bg} alt="" className={`ps-cover-fullscreen-bg${current.id === '00' ? ' ps-bg-cover' : ''}`} aria-hidden="true" />
            )}
            <div className="ps-slide-wrap">
              {current.content}
            </div>
          </div>

          <div className="ps-controls">
            <a href={PDF_URL} className="ps-download-btn" download>DOWNLOAD PDF</a>
            <div className="ps-ctrl-nav">
              <button
                className={`slider-btn ps-arrow${isFirst ? ' ps-arrow-disabled' : ''}`}
                onClick={prev}
                disabled={isFirst}
                aria-label="Previous slide"
              >
                <span className="material-symbols-rounded">chevron_left</span>
              </button>
              <span className="ps-counter">
                {String(slide + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}
              </span>
              <button
                className={`slider-btn ps-arrow${isLast ? ' ps-arrow-disabled' : ''}`}
                onClick={next}
                disabled={isLast}
                aria-label="Next slide"
              >
                <span className="material-symbols-rounded">chevron_right</span>
              </button>
            </div>
            <div className="ps-controls-spacer" />
          </div>
        </div>
      </div>
    </>
  );
}
